import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  GKDatePicker,
  GKModal,
  GKSearchSelect,
  GKSelect,
  GKTextArea,
  GKTextField,
  Grid,
  MenuItem,
  Switch,
  TextField,
} from "components";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useEffect } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { BILLING_STATUS } from "utils/constants";

type LocationOption = {
  id: string;
  name: string;
};
interface InvoiceDetail {
  // invoiceNumber: string;
  // invoiceItems: string[];
  country: LocationOption;
  state: LocationOption;
  city: LocationOption;
  pincode: string;
  address: string;
  remarks: string;
}

interface BillingFormValues {
  amcCharges: string;
  billingDate: string;
  portfolioCount: string;
  freePortfolioCount: string;
  perPortfolioCharges: string;
  status: string;
  walletTopup: string;
  invoiceNumber: string;
  invoiceDate: string;
  generateInvoice: boolean;
  invoiceDetails: InvoiceDetail;
}

interface Props {
  inModuleOpen: boolean;
  setIsModuleOpen: (open: boolean) => void;
  refetch: () => void;
  openModule: { id?: string } | null;
  revenueData: any;
  revenueRefetch: () => void;
}

const BillingModal = (props: Props) => {
  const {
    inModuleOpen,
    setIsModuleOpen,
    refetch,
    openModule,
    revenueData,
    revenueRefetch,
  } = props;

  const { id } = useParams();

  const { data: adminDetails } = useQuery({
    queryKey: ["GET_ADMIN_DETAILS"],
    queryFn: () => axiosInstance.get(`/super_admin/${id}/advisor-details/`),
  });

  const advisorDetails = adminDetails?.data?.data;

  const { mutate: createBillingMutate, isLoading } = useMutation({
    mutationKey: ["CREATE_BILLING"],
    mutationFn: (data: any) =>
      axiosInstance.post(`/super_admin/${id}/billing-details/`, data),
    onSuccess: (response) => {
      refetch();
      revenueRefetch();
      toast.success(response?.data?.message || "Broker Update Successfully");
      setIsModuleOpen(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const { mutate: updateBillingDetailsMutate } = useMutation({
    mutationKey: ["UPDATE_BILLING_DETAILS"],
    mutationFn: (data: any) =>
      axiosInstance.patch(`/super_admin/${id}/update-billing-details/`, data),
    onSuccess: (response) => {
      refetch();
      toast.success(response?.data?.message || "Broker Update Successfully");
      setIsModuleOpen(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const formik = useFormik<BillingFormValues>({
    enableReinitialize: true,
    initialValues: {
      amcCharges: revenueData?.data?.data?.amcCharges || "",
      billingDate: revenueData?.data?.data?.renewalDate || "",
      portfolioCount: revenueData?.data?.data?.portfolioLimit || "",
      freePortfolioCount: "",
      perPortfolioCharges: revenueData?.data?.data?.perPortfolioCharges || "",
      status: "",
      walletTopup: "",
      invoiceNumber: advisorDetails?.invoiceNumber || "",
      invoiceDate: advisorDetails?.invoiceDate || "",
      generateInvoice: false,
      invoiceDetails: {
        // invoiceNumber: "",
        // invoiceItems: [],
        country: {} as any,
        state: {} as any,
        city: {} as any,
        pincode: advisorDetails?.pincode || "",
        address: advisorDetails?.address || "",
        remarks: "",
      },
    },
    onSubmit: (values: BillingFormValues) => {
      const transformedInvoiceDetails = {
        ...values.invoiceDetails,
        country: values.invoiceDetails.country?.name || "",
        state: values.invoiceDetails.state?.name || "",
        city: values.invoiceDetails.city?.name || "",
      };

      const formattedValues = {
        ...values,
        billingDate: values.billingDate
          ? dayjs(values.billingDate).format("YYYY-MM-DD")
          : "",
      };

      if (openModule?.id) {
        updateBillingDetailsMutate({
          id: openModule.id,
          status: values.status,
        });
      } else {
        const { status, ...rest } = formattedValues;
        createBillingMutate({
          ...rest,
          invoiceDetails: transformedInvoiceDetails,
        });
      }
    },
  });

  const { data: countryData } = useQuery({
    queryKey: ["GET_ALL_COUNTRY"],
    queryFn: () => axiosInstance.get(`/country/`),
  });

  const { data: stateData } = useQuery({
    queryKey: [
      "GET_STATE_BY_COUNTRY",
      formik.values.invoiceDetails?.country?.id,
    ],
    queryFn: () =>
      axiosInstance.get(
        `/state/?country_id=${formik.values.invoiceDetails?.country?.id}`
      ),
    enabled: !!formik.values.invoiceDetails?.country?.id,
  });

  const { data: cityData } = useQuery({
    queryKey: ["GET_STATE_BY_CITY", formik.values.invoiceDetails?.state?.id],
    queryFn: () =>
      axiosInstance.get(
        `/city/?state_id=${formik.values.invoiceDetails?.state?.id}`
      ),
    enabled: !!formik.values.invoiceDetails?.state?.id,
  });

  // Prefill empty country/state/city from advisor details by matching names
  useEffect(() => {
    const options = { country: countryData, state: stateData, city: cityData };

    (["country", "state", "city"] as const).forEach((key) => {
      if (formik.values.invoiceDetails?.[key]?.id || !advisorDetails?.[key])
        return;
      const match = options[key]?.data?.data?.find(
        (item: LocationOption) =>
          item?.name?.toLowerCase() === advisorDetails[key].toLowerCase()
      );
      if (match) formik.setFieldValue(`invoiceDetails.${key}`, match);
    });
  }, [countryData, stateData, cityData, advisorDetails]);

  return (
    <GKModal
      open={inModuleOpen}
      setOpen={setIsModuleOpen}
      modalTitle={
        openModule?.id ? "Update Billing Details" : "Add Billing Details"
      }
      footer={
        <Grid
          container
          spacing={2}
          justifyContent={"flex-end"}
          alignItems={"center"}
        >
          <Grid item xs={6} display="flex" alignItems="center">
            {!openModule?.id && (
              <FormControlLabel
                control={
                  <Switch
                    name="generateInvoice"
                    checked={formik.values.generateInvoice === true}
                    onChange={(e) =>
                      formik.setFieldValue("generateInvoice", e.target.checked)
                    }
                  />
                }
                label="Generate Invoice"
              />
            )}
          </Grid>

          <Grid item xs={6} container justifyContent="flex-end">
            <Button
              type="submit"
              form="billing-details-form"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : openModule?.id ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </Grid>
        </Grid>
      }
    >
      <form onSubmit={formik.handleSubmit} id="billing-details-form">
        {!openModule?.id && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl variant="standard" fullWidth>
                <GKTextField
                  type="number"
                  inputLabel="Amc Charges"
                  onChange={formik?.handleChange}
                  value={formik.values.amcCharges}
                  name="amcCharges"
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl variant="standard" fullWidth>
                <GKDatePicker
                  inputLabel="Billing Date"
                  name="billingDate"
                  value={
                    formik.values.billingDate
                      ? dayjs(formik.values.billingDate)
                      : null
                  }
                  onChange={(value) => {
                    formik.setFieldValue(
                      "billingDate",
                      value ? dayjs(value).format("YYYY-MM-DD") : ""
                    );
                  }}
                  disableFuture
                  slotProps={{
                    textField: {
                      error: false,
                      helperText: false,
                    },
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl variant="standard" fullWidth>
                <GKTextField
                  type="number"
                  inputLabel="Portfolio Count"
                  onChange={formik?.handleChange}
                  value={formik.values.portfolioCount}
                  name="portfolioCount"
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl variant="standard" fullWidth>
                <GKTextField
                  type="number"
                  inputLabel="Free Portfolio Count"
                  onChange={formik?.handleChange}
                  value={formik.values.freePortfolioCount}
                  name="freePortfolioCount"
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl variant="standard" fullWidth>
                <GKTextField
                  type="number"
                  inputLabel="Per Portfolio Charges"
                  onChange={formik?.handleChange}
                  value={formik.values.perPortfolioCharges}
                  name="perPortfolioCharges"
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl variant="standard" fullWidth>
                <GKTextField
                  type="number"
                  inputLabel="Wallet Topup"
                  onChange={formik?.handleChange}
                  value={formik.values.walletTopup}
                  name="walletTopup"
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl variant="standard" fullWidth>
                <GKTextField
                  type="text"
                  inputLabel="Invoice Number"
                  onChange={formik?.handleChange}
                  value={formik.values.invoiceNumber}
                  name="invoiceNumber"
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl variant="standard" fullWidth>
                <GKDatePicker
                  inputLabel="Invoice Date"
                  name="invoiceDate"
                  value={
                    formik.values.invoiceDate
                      ? dayjs(formik.values.invoiceDate)
                      : null
                  }
                  onChange={(value) => {
                    formik.setFieldValue(
                      "invoiceDate",
                      value ? dayjs(value).format("YYYY-MM-DD") : ""
                    );
                  }}
                  slotProps={{
                    textField: {
                      error: false,
                      helperText: false,
                    },
                  }}
                />
              </FormControl>
            </Grid>
            {formik.values.generateInvoice === true && (
              <>
                <Grid item xs={12}>
                  <FormControl variant="standard" fullWidth>
                    <GKTextField
                      type="text"
                      inputLabel="Address"
                      name="invoiceDetails.address"
                      onChange={formik.handleChange}
                      value={formik.values.invoiceDetails?.address}
                    />
                  </FormControl>
                </Grid>
                {/* <Grid item xs={6}>
                  <FormControl variant="standard" fullWidth>
                    <GKTextField
                      type="text"
                      inputLabel="Invoice Number"
                      name="invoiceDetails.invoiceNumber"
                      onChange={formik.handleChange}
                      value={formik.values.invoiceDetails?.invoiceNumber}
                    />
                  </FormControl>
                </Grid> */}
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={3}>
                    <GKSearchSelect
                      inputLabel="Country"
                      options={countryData?.data?.data || []}
                      value={
                        formik.values.invoiceDetails?.country?.id
                          ? formik.values.invoiceDetails.country
                          : null
                      }
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      onChange={(event, val) => {
                        formik.setFieldValue("invoiceDetails.country", val);
                        formik.setFieldValue("invoiceDetails.state", {});
                        formik.setFieldValue("invoiceDetails.city", {});
                      }}
                      getOptionLabel={(opt) =>
                        typeof opt === "object" ? opt.name : String(opt)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          variant="outlined"
                          placeholder="Select Country"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <GKSearchSelect
                      inputLabel="State"
                      options={stateData?.data?.data || []}
                      value={
                        formik.values.invoiceDetails?.state?.id
                          ? formik.values.invoiceDetails.state
                          : null
                      }
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      onChange={(event, val) => {
                        formik.setFieldValue("invoiceDetails.state", val);
                        formik.setFieldValue("invoiceDetails.city", {});
                      }}
                      getOptionLabel={(opt) =>
                        typeof opt === "object" ? opt.name : String(opt)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          variant="outlined"
                          placeholder="Select State"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <GKSearchSelect
                      inputLabel="City"
                      options={cityData?.data?.data || []}
                      value={
                        formik.values.invoiceDetails?.city?.id
                          ? formik.values.invoiceDetails.city
                          : null
                      }
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      onChange={(event, val) =>
                        formik.setFieldValue("invoiceDetails.city", val)
                      }
                      getOptionLabel={(opt) =>
                        typeof opt === "object" ? opt.name : String(opt)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          variant="outlined"
                          placeholder="Select City"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl variant="standard" fullWidth>
                      <GKTextField
                        type="number"
                        inputLabel="Pincode"
                        name="invoiceDetails.pincode"
                        onChange={formik.handleChange}
                        value={formik.values.invoiceDetails?.pincode}
                      />
                    </FormControl>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <GKTextArea
                    minRows={4}
                    inputLabel="Remarks"
                    name="invoiceDetails.remarks"
                    placeholder="Remarks"
                    onChange={formik.handleChange}
                    value={formik.values.invoiceDetails?.remarks}
                  />
                </Grid>

                {/* <Grid item xs={12} container justifyContent="space-between">
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <GKCheckbox
                          name="invoiceDetails.invoiceItems"
                          checked={formik.values.invoiceDetails.invoiceItems.includes(
                            "wallet_topup"
                          )}
                          onChange={(e) => {
                            const { checked } = e.target;
                            const current =
                              formik.values.invoiceDetails.invoiceItems;
                            const updated = checked
                              ? [...current, "wallet_topup"]
                              : current.filter(
                                  (item) => item !== "wallet_topup"
                                );
                            formik.setFieldValue(
                              "invoiceDetails.invoiceItems",
                              updated
                            );
                          }}
                        />
                      }
                      label="Wallet Topup"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <GKCheckbox
                          name="invoiceDetails.invoiceItems"
                          checked={formik.values.invoiceDetails.invoiceItems.includes(
                            "amc_charges"
                          )}
                          onChange={(e) => {
                            const { checked } = e.target;
                            const current =
                              formik.values.invoiceDetails.invoiceItems;
                            const updated = checked
                              ? [...current, "amc_charges"]
                              : current.filter(
                                  (item) => item !== "amc_charges"
                                );
                            formik.setFieldValue(
                              "invoiceDetails.invoiceItems",
                              updated
                            );
                          }}
                        />
                      }
                      label="AMC Charges"
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} container justifyContent="space-between">
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <GKCheckbox
                          name="invoiceDetails.invoiceItems"
                          checked={formik.values.invoiceDetails.invoiceItems.includes(
                            "portfolio_charges"
                          )}
                          onChange={(e) => {
                            const { checked } = e.target;
                            const current =
                              formik.values.invoiceDetails.invoiceItems;
                            const updated = checked
                              ? [...current, "portfolio_charges"]
                              : current.filter(
                                  (item) => item !== "portfolio_charges"
                                );
                            formik.setFieldValue(
                              "invoiceDetails.invoiceItems",
                              updated
                            );
                          }}
                        />
                      }
                      label="Portfolio Charges"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <GKCheckbox
                          name="invoiceDetails.invoiceItems"
                          checked={formik.values.invoiceDetails.invoiceItems.includes(
                            "amc_charges_including_portfolio_charges"
                          )}
                          onChange={(e) => {
                            const { checked } = e.target;
                            const current =
                              formik.values.invoiceDetails.invoiceItems;
                            const updated = checked
                              ? [
                                  ...current,
                                  "amc_charges_including_portfolio_charges",
                                ]
                              : current.filter(
                                  (item) =>
                                    item !==
                                    "amc_charges_including_portfolio_charges"
                                );
                            formik.setFieldValue(
                              "invoiceDetails.invoiceItems",
                              updated
                            );
                          }}
                        />
                      }
                      label="AMC Charges Including Portfolio Charges"
                    />
                  </Grid>
                </Grid> */}
              </>
            )}
          </Grid>
        )}
        {openModule?.id && !formik.values.generateInvoice && (
          <>
            <Grid item xs={12}>
              <FormControl variant="standard" fullWidth>
                <GKSelect
                  inputLabel="Status"
                  onChange={formik?.handleChange}
                  value={formik.values.status}
                  name="status"
                >
                  {BILLING_STATUS.map((dataItem, i) => (
                    <MenuItem key={i} value={dataItem.value}>
                      {dataItem.name}
                    </MenuItem>
                  ))}
                </GKSelect>
              </FormControl>
            </Grid>
          </>
        )}
      </form>
    </GKModal>
  );
};

export default BillingModal;
